import { useState } from 'react';
import { getRtkErrorMessage } from 'api/getRtkErrorMessage';
import { useAppDispatch } from 'slices/store';
import { TAffiliation } from '@timeedit/types/lib/types';
import {
  authConfigApi,
  useCreateAffiliationMutation,
} from '../api/authConfig.api';
import {
  AffiliationFieldKey,
  buildAffiliationValidationMessage,
  validateAffiliationFields,
} from './validateAffiliationFields';

export type { AffiliationFieldKey };

export type BulkCreateItem = {
  rowIdx: number;
  label: string;
  affiliation: Omit<TAffiliation, 'authConfigId'>;
};

export type BulkCreateResult = {
  rowIdx: number;
  label: string;
  error?: string;
  /**
   * Present when the failure was caught by frontend validation.
   * Identifies the specific TAffiliation fields that are invalid so the table
   * can show targeted error borders on the corresponding input cells.
   * When absent on a failed result the table falls back to a row-level border.
   */
  fieldErrors?: Set<AffiliationFieldKey>;
};

/**
 * Sequentially creates affiliations with progress reporting, then invalidates
 * the `AuthAffiliations` list once at the end. Encapsulates the
 * `skipInvalidation` opt-out so individual call sites can't forget to
 * invalidate.
 *
 * Sequential (not parallel) on purpose: keeps the table's infinite-scroll
 * hook from thrashing mid-batch and gives the user a meaningful progress bar
 * while also maintaining the order of creation.
 */
export const useBulkCreateAffiliations = () => {
  const [createAffiliation] = useCreateAffiliationMutation();
  const dispatch = useAppDispatch();

  const [isRunning, setIsRunning] = useState(false);
  const [progress, setProgress] = useState(0);

  const validateItems = (items: BulkCreateItem[]): BulkCreateResult[] => {
    const results: BulkCreateResult[] = [];
    items.forEach(({ rowIdx, label, affiliation }) => {
      const errors = validateAffiliationFields(affiliation);
      if (errors.size > 0) {
        results.push({
          rowIdx,
          label,
          error: buildAffiliationValidationMessage(affiliation),
          fieldErrors: errors,
        });
      }
    });
    return results;
  };

  const run = async (
    authConfigId: string,
    items: BulkCreateItem[]
  ): Promise<BulkCreateResult[]> => {
    setIsRunning(true);
    setProgress(0);

    const results: BulkCreateResult[] = [];

    try {
      const validationResults = validateItems(items);
      if (validationResults.length > 0) {
        return validationResults;
      }

      for (let i = 0; i < items.length; i += 1) {
        const { rowIdx, label, affiliation } = items[i];

        try {
          await createAffiliation({
            authConfigId,
            affiliation,
            skipInvalidation: true,
          }).unwrap();
          results.push({ rowIdx, label });
        } catch (err) {
          // No fieldErrors — the failure is API-only (e.g. org ID not found).
          // The table will fall back to a row-level error border.
          results.push({
            rowIdx,
            label,
            error: getRtkErrorMessage(err) || 'Unknown error',
          });
        }

        setProgress(Math.round(((i + 1) / items.length) * 100));
      }
    } finally {
      // Always invalidate, even if the loop throws unexpectedly, so the list
      // reflects whatever did get created.
      dispatch(
        authConfigApi.util.invalidateTags([
          { type: 'AuthAffiliations', id: authConfigId },
        ])
      );
      setIsRunning(false);
    }

    return results;
  };

  return { run, validateItems, isRunning, progress };
};
